// src/components/AudioAssistToggle.tsx - Updated for on-demand audio
import React, { useState } from 'react';
import { IconButton, Tooltip, Box, Typography, Switch, Menu, MenuItem } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import CachedIcon from '@mui/icons-material/Cached';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useI18n } from '../hooks/useI18n'; // Import the i18n hook
import '../styles/MessageAudio.css';

interface AudioAssistToggleProps {
  isEnabled: boolean;
  isSupported: boolean;
  hasPlayingMessage: boolean;
  onToggle: () => void;
  onStopAll?: () => void;
  onClearCache?: () => void;
  cachedAudioCount?: number;
  compact?: boolean;
}

const AudioAssistToggle: React.FC<AudioAssistToggleProps> = ({
  isEnabled,
  isSupported,
  hasPlayingMessage,
  onToggle,
  onStopAll,
  onClearCache,
  cachedAudioCount = 0,
  compact = false,
}) => {
  const t = useI18n(); // Initialize the translation hook
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleClearCache = () => {
    if (onClearCache) {
      onClearCache();
    }
    handleMenuClose();
  };

  if (!isSupported) {
    return (
      <Tooltip title="Audio assist is not supported in this browser">
        <Box className="audio-assist-not-supported">
          <VolumeOffIcon fontSize="small" />
          {!compact && (
            <Typography variant="caption" className="audio-assist-status-text">
              Not supported
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  if (compact) {
    return (
      <Box className="audio-assist-compact-toggle">
        <Tooltip title={isEnabled ? 'Disable audio assist' : 'Enable audio assist'}>
          <IconButton
            size="small"
            onClick={onToggle}
            color={isEnabled ? 'primary' : 'default'}
            className="audio-assist-toggle-button"
          >
            {isEnabled ? (
              hasPlayingMessage ? (
                <VolumeUpIcon fontSize="small" />
              ) : (
                <VolumeDownIcon fontSize="small" />
              )
            ) : (
              <VolumeOffIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        {isEnabled && (
          <Tooltip title="Audio options">
            <IconButton
              size="small"
              onClick={handleMenuClick}
              className="audio-assist-options-button"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={handleMenuClose}>
          {hasPlayingMessage && onStopAll && (
            <MenuItem
              onClick={() => {
                onStopAll();
                handleMenuClose();
              }}
            >
              <VolumeOffIcon fontSize="small" sx={{ mr: 1 }} />
              Stop playback
            </MenuItem>
          )}

          {onClearCache && (
            <MenuItem onClick={handleClearCache} disabled={cachedAudioCount === 0}>
              <CachedIcon fontSize="small" sx={{ mr: 1 }} />
              Clear audio cache {cachedAudioCount > 0 ? `(${cachedAudioCount})` : ''}
            </MenuItem>
          )}
        </Menu>
      </Box>
    );
  }

  return (
    <Box
      className="audio-assist-header-toggle"
      sx={{
        width: '100%',
        backgroundColor: '#ffffff',
        px: 2,
        py: 1,
        borderRadius: 1,
        boxSizing: 'border-box',
        marginLeft: '-1rem',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Switch checked={isEnabled} onChange={onToggle} size="small" color="primary" />
        <Typography variant="body2" sx={{ color: '#242424' }}>
          {t.get('audioAssistLabel')}
        </Typography>
      </Box>
    </Box>
  );
};

export default AudioAssistToggle;
